/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useState } from 'react';

/* Import components */
import CodeBlock from '@/components/code/CodeBlock';
import LayoutSectionHeader from '@/components/tabs/layouts/LayoutSectionHeader';
import KpiCarouselStatic from '@/components/kpi/KpiCarouselStatic';
import KpiValueStatic from '@/components/kpi/KpiValueStatic';

/* Import configuration */
import {
    KPI_CAROUSEL_STATIC_EXAMPLES,
    KPI_VALUE_STATIC_EXAMPLES,
} from '@/components/tabs/layouts/layoutExamples';

interface KpiStaticLayoutsTabProps {
    theme: 'light' | 'dark';
}

function KpiStaticLayoutsTab({ theme }: KpiStaticLayoutsTabProps) {
    const [selectedKpiValueStaticExampleIndex, setSelectedKpiValueStaticExampleIndex] = useState(0);
    const [selectedKpiCarouselStaticExampleIndex, setSelectedKpiCarouselStaticExampleIndex] = useState(0);
    const selectedKpiValueStaticExample = KPI_VALUE_STATIC_EXAMPLES[selectedKpiValueStaticExampleIndex];
    const selectedKpiCarouselStaticExample = KPI_CAROUSEL_STATIC_EXAMPLES[selectedKpiCarouselStaticExampleIndex];

    return (
        <div className="layouts-tab">
            <LayoutSectionHeader sectionKey="kpi-static" />

            <section className="layouts-section">
                <h3>KpiValueStatic</h3>
                <p>Renders a manually provided value with the shared KPI presentation.</p>
                <div className="inline-flex flex-wrap items-center gap-3">
                    <label className="text-sm font-semibold" htmlFor="kpi-value-static-example">
                        Static value example
                    </label>
                    <select
                        id="kpi-value-static-example"
                        className="select select-bordered w-auto"
                        value={selectedKpiValueStaticExampleIndex}
                        onChange={(event) => {
                            setSelectedKpiValueStaticExampleIndex(Number(event.target.value));
                        }}
                    >
                        {KPI_VALUE_STATIC_EXAMPLES.map((example, index) => (
                            <option key={example.label} value={index}>{example.label}</option>
                        ))}
                    </select>
                </div>
                <h4>Code example</h4>
                <div className="layouts-code-tabs">
                    <div className="tabs tabs-lift"><button type="button" className="tab tab-active">JSX</button></div>
                    <CodeBlock className="layouts-code--tabbed" roundedTop={false} code={selectedKpiValueStaticExample.code} theme={theme} />
                </div>
                <h4>Rendered preview</h4>
                <section className="dashboard-panel layouts-demo-panel layouts-demo-panel--value">
                    <KpiValueStatic
                        value={selectedKpiValueStaticExample.props.value}
                        title={selectedKpiValueStaticExample.props.title}
                        unit={selectedKpiValueStaticExample.props.unit}
                        conversion={selectedKpiValueStaticExample.props.conversion}
                        date={selectedKpiValueStaticExample.props.date}
                        info={selectedKpiValueStaticExample.props.info}
                        link={selectedKpiValueStaticExample.props.link}
                        modal={selectedKpiValueStaticExample.props.modal}
                        trafficLight={selectedKpiValueStaticExample.props.trafficLight}
                        fractionDigits={selectedKpiValueStaticExample.props.fractionDigits}
                        showLegend={selectedKpiValueStaticExample.props.showLegend}
                    />
                </section>
            </section>

            <section className="layouts-section">
                <h3>KpiCarouselStatic</h3>
                <p>Renders multiple manually provided KPI values in a shared carousel.</p>
                <div className="inline-flex flex-wrap items-center gap-3">
                    <label className="text-sm font-semibold" htmlFor="kpi-carousel-static-example">
                        Static carousel example
                    </label>
                    <select
                        id="kpi-carousel-static-example"
                        className="select select-bordered w-auto"
                        value={selectedKpiCarouselStaticExampleIndex}
                        onChange={(event) => {
                            setSelectedKpiCarouselStaticExampleIndex(Number(event.target.value));
                        }}
                    >
                        {KPI_CAROUSEL_STATIC_EXAMPLES.map((example, index) => (
                            <option key={example.label} value={index}>{example.label}</option>
                        ))}
                    </select>
                </div>
                <h4>Code example</h4>
                <div className="layouts-code-tabs">
                    <div className="tabs tabs-lift"><button type="button" className="tab tab-active">JSX</button></div>
                    <CodeBlock className="layouts-code--tabbed" roundedTop={false} code={selectedKpiCarouselStaticExample.code} theme={theme} />
                </div>
                <h4>Rendered preview</h4>
                <section className="dashboard-panel layouts-demo-panel layouts-demo-panel--value">
                    <KpiCarouselStatic
                        autoAdvanceMs={selectedKpiCarouselStaticExample.props.autoAdvanceMs}
                        info={selectedKpiCarouselStaticExample.props.info}
                        items={selectedKpiCarouselStaticExample.props.items}
                        showLegend={selectedKpiCarouselStaticExample.props.showLegend}
                    />
                </section>
            </section>
        </div>
    );
}

export default KpiStaticLayoutsTab;
